import React, { useState } from "react";
import { HiFolderOpen, HiPencil } from "react-icons/hi";
import { CustomButton } from "./CustomButton";
import { CustomInput } from "./CustomInput";
import { useForm } from "react-hook-form";
import { CharacterCounter } from "./CharacterCounter";
import { useTranslation } from "react-i18next";
import { sanitizeAndTrim } from "../../utils/fieldUtils";
import NoTranslate from "./NoTranslate";

interface FolderHeaderProps {
  folderName: string;
  onUpdate?: (newFolderName: string) => void;
  isUpdating?: boolean;
}

interface EditFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFolderName: string;
  onUpdate: (newFolderName: string) => void;
  isUpdating?: boolean;
}

const EditFolderModal: React.FC<EditFolderModalProps> = ({
  isOpen,
  onClose,
  currentFolderName,
  onUpdate,
  isUpdating = false,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      folderName: currentFolderName,
    },
  });

  const onSubmit = (data: { folderName: string }) => {
    if (data.folderName && data.folderName.trim() !== "") {
      onUpdate(data.folderName.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t("dashboard.FolderHeader.editFolderName")}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <CustomInput
                name="folderName"
                control={control}
                label={t("dashboard.FolderHeader.folderNameLabel") as string}
                placeholder={
                  t("dashboard.FolderHeader.folderNamePlaceholder") as string
                }
                onValueChange={(e) => {
                  const sanitized = sanitizeAndTrim(e.target.value);
                  setValue("folderName", sanitized);
                }}
                maxLength={50}
              />
              <p className="text-xs text-gray-500 mt-1 mb-2">
                {t("common.folderNameHint")}
              </p>
              <CharacterCounter
                currentLength={watch("folderName")?.length || 0}
                maxLength={50}
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                disabled={isUpdating}
              >
                {t("common.cancel")}
              </button>
              <CustomButton
                type="submit"
                variant="primary"
                title={t("common.update") as string}
                disabled={
                  isUpdating ||
                  !watch("folderName")?.trim() ||
                  watch("folderName") === currentFolderName
                }
                isLoading={isUpdating}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const FolderHeader: React.FC<FolderHeaderProps> = ({
  folderName,
  onUpdate,
  isUpdating = false,
}) => {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleUpdate = (newFolderName: string) => {
    if (onUpdate) {
      onUpdate(newFolderName);
      setTimeout(() => setIsEditModalOpen(false), 500);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <HiFolderOpen className="shrink-0 text-blue-600" size={20} />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-semibold text-gray-900">
              <NoTranslate>{folderName}</NoTranslate>
            </h3>
            <p className="text-xs text-gray-500">
              {t("dashboard.FolderHeader.manageFields")}
            </p>
          </div>
        </div>
        {onUpdate && (
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-blue-600"
            title={t("dashboard.FolderHeader.editFolderTitle") as string}
          >
            <HiPencil size={18} />
          </button>
        )}
      </div>

      <EditFolderModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentFolderName={folderName}
        onUpdate={handleUpdate}
        isUpdating={isUpdating}
      />
    </>
  );
};
